/* eslint-disable react/no-array-index-key */
import React, {
  useCallback, useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  Route, Redirect, Switch, useHistory, useLocation,
} from 'react-router-dom';
import { withI18n } from 'react-i18next';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import shortid from 'shortid';
import {
  MainProps, RootState,
  BreadCrumbsProps,
} from './Main.types';
import Template from '@/components/Layout/Template/index';
import { onChangeRouteEventTagManager } from '@/utils/ThirdParties/tagManager';
import { setBreadCrumb } from '@/redux/actions/breadCrumbs';
import redirects from '@/routes/redirects';
import generateBreadCrumbs from '@/routes/generateBreadCrumbs';
import NewSitePage from '@/pages/NewSite/NewSitePage';
import FeedbackSite from '@/pages/NewSite/FeedbackSite/index';
import Error404Page from '@/pages/Error404Page/index';
import RedirectToWHMPage from '@/pages/SSO/RedirectToWHMPage';
import RedirectToSiteBuilderPage from '@/pages/SSO/RedirectToSiteBuilderPage/index';
import { handleLastPath } from '@/utils/Formatters/lastPath';
import { commonActions, productsActions } from '@/redux/modules/';
import Loader from '@/components/Layout/Loader';
import { queryParams } from '@/utils/Application/queryParams';
import {
  BRAVO_CHANGE_PHONE_FLOW, BRAVO_EMAIL_FLOW,
  EXPERIENCE_ACCOUNT_SECURITY,
  ENABLE_NEW_ADVANCED_HOSTING_PAGE,
} from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import * as featureFlags from '@/config/GrowthBook/constants';
import { Growthbook } from '@/config/GrowthBook';
import { OnboardingHandler } from '../Onboarding/OnboardingHandler';
import { locale } from '@/utils/locale';
import { useAiChat } from '@/hooks/AiChat';
import { AiChatWrapper } from '@/components/Layout/Template/AiChatWrapper';
import { routesV2 as routes } from '@/routes/routes';
import EmailVerify from '../EmailVerify';
import PhoneVerify from '../PhoneVerify';

const Main = ({ t }: MainProps) => {
  const onChangeRouteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyListenKeyRef = useRef<string>('');

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const newSiteFlux = useSelector((state: RootState) => !state.summary.websiteBuilderDismiss && state.summary.newSiteFlux);
  const emailFlowSkip = useSelector((state: RootState) => state.emailVerify.emailFlowSkip);
  const emailChangeFlow = useSelector((state: RootState) => state.emailVerify.emailChangeFlow);
  const emailVerified = useSelector((state: RootState) => state.summary.emailVerified);
  const phoneNumberVerified = useSelector((state: RootState) => state.summary.phoneNumberVerified);
  const phoneNumberFlowSkip = useSelector((state: RootState) => state.phoneNumberVerify.phoneNumberFlowSkip);
  const forceUrl = useSelector((state: RootState) => state.phoneNumberVerify.forceUrl);
  const phoneNumberChangeFlow = useSelector((state: RootState) => state.phoneNumberVerify.phoneNumberChangeFlow);
  const dynamicBreadCrumbs = useSelector((state: RootState) => state.breadCrumbs.dynamicBreadCrumbTitles);
  const { currentChat: currentAiChat } = useSelector((state: RootState) => state.merlin);
  const bravoEnableEmailFlow = useFeatureIsOn(BRAVO_EMAIL_FLOW);
  const features = useSelector((state: RootState) => state.summary.features);
  const bravoEnablePhoneFlow = useFeatureIsOn(BRAVO_CHANGE_PHONE_FLOW);
  const renderAccountSecurity = useFeatureIsOn(EXPERIENCE_ACCOUNT_SECURITY);
  const enableNewScreenAdvancedHosting = useFeatureIsOn(ENABLE_NEW_ADVANCED_HOSTING_PAGE);
  const sitesOnboardingEnabled = useFeatureIsOn(featureFlags.ATIVACAO_SITES_ONBOARDING);
  const useOnboardingAsWizard = useFeatureIsOn(featureFlags.ATIVACAO_ONBOARDING_INSIDE_SITES_WIZARD);
  const enableAdsPage = useFeatureIsOn(featureFlags.ATIVACAO_ENABLE_ADS_PAGE);
  const enableGatorBackup = useFeatureIsOn(featureFlags.ATIVACAO_ENABLE_GATOR_BACKUP_FEATURE);

  const featureTogglePhoneNumberFlow = useSelector((state: RootState) => state.featureToggles.toggles && state.featureToggles.toggles.portal.phoneNumberFlow);
  const featureToggleEmailNumberFlow = useSelector((state: RootState) => state.featureToggles.toggles && state.featureToggles.toggles.portal.emailFlow);
  const {
    id: userId,
    onboarding,
  } = useSelector((state: RootState) => state.summary);
  const products = useSelector((state: RootState) => state.products);
  const isRootRoute = history.location.pathname === '/';

  const { handleAiChatRender } = useAiChat();

  const ssoRedirectWHMP = history.location.pathname.includes(t('routes.whmRedirectAccess'));
  const ssoRedirectSiteBuilder = history.location.pathname.includes(t('routes.siteBuilderRedirectAccess'));

  const pathEmailsList = `${t('routes.emailsList')}`;
  const pathTitanUpgrade = `${t('routes.titanUpgrade')}/`;
  const pathTitanInboxUpgrade = `${t('routes.titanInboxUpgrade')}/`;

  const isTitanUrl = location.pathname.includes(pathEmailsList) || location.pathname.includes(pathEmailsList.concat(pathTitanUpgrade)) || location.pathname.includes(pathEmailsList.concat(pathTitanInboxUpgrade));

  const sendFlagsToRedux = useCallback(() => {
    const flags = Object.keys(featureFlags).reduce(
      (list, flag) => ({
        ...list,
        [flag.toLowerCase()]: Growthbook.evalFeature(flag.toLowerCase()).value,
      }),
      {},
    );

    if (flags) {
      dispatch(commonActions.toggles.set(flags));
    }
  }, [dispatch]);

  useEffect(() => {
    sendFlagsToRedux();
  }, [sendFlagsToRedux]);

  const getLocationValue = useCallback(() => {
    const locationSearch = queryParams(location.search, 'location');

    if (locationSearch !== null) {
      return locationSearch;
    }

    let locationValue = (location.pathname + location.search).replace(/\?/g, '&');
    locationValue = locationValue.startsWith('/') ? locationValue.slice(1) : locationValue;
    locationValue = locationValue.replace(/\//g, '+');
    return locationValue;
  }, [location.pathname, location.search]);

  const allRoutes = useMemo(() => routes(t, undefined, {
    renderAccountSecurity,
    enableNewScreenAdvancedHosting,
    useOnboardingAsWizard,
    enableAdsPage,
    enableGatorProtect: !!features?.gator_protect,
    enableGatorBackup,
  }).map(({
    path, Component, full, beta,
  }) => (
    <Route
      exact
      path={path}
      key={shortid.generate()}
      render={(props: any) => (full ? (
        <Component {...props} />
      ) : (
        <Template beta={beta}>
          <AiChatWrapper>
            {getBreadCrumbs(props, Component)}
          </AiChatWrapper>
        </Template>
      ))}
    />
  )), [t, renderAccountSecurity, useOnboardingAsWizard, enableAdsPage, enableNewScreenAdvancedHosting, features?.gator_protect, enableGatorBackup]);

  useEffect(() => {
    if (location.pathname !== '/') {
      handleLastPath(location, userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action === 'PUSH') {
        handleLastPath(location, userId);
      }
    });

    return () => {
      unlisten();
    };
  }, [history, userId]);

  useEffect(() => {
    const unlisten = history.listen((historyListen) => {
      if (historyListenKeyRef.current !== historyListen.key) {
        if (onChangeRouteTimeoutRef.current) clearTimeout(onChangeRouteTimeoutRef.current);
        historyListenKeyRef.current = historyListen.key || '';
        onChangeRouteTimeoutRef.current = setTimeout(() => {
          onChangeRouteEventTagManager();
        }, 100);
      }
    });

    return () => {
      unlisten();
      if (onChangeRouteTimeoutRef.current) clearTimeout(onChangeRouteTimeoutRef.current);
    };
  }, [history]);

  const getBreadCrumbs = (props: BreadCrumbsProps, Component: any) => {
    const isMobile = window.innerWidth < 700;
    const { match } = props;

    if (match.path === '*' && !isMobile) {
      return React.createElement(Component, props);
    }

    const breadCrumbs = generateBreadCrumbs(props, t, dynamicBreadCrumbs);
    dispatch(setBreadCrumb(breadCrumbs));

    return React.createElement(Component, props);
  };

  useEffect(() => {
    if (isRootRoute) {
      dispatch(productsActions.products.request({ history }));
    }
  }, [dispatch, history, isRootRoute]);

  const getRender = () => {
    const isInSitebuilderSsoPage = history.location.pathname.includes(`${locale('routes.siteBuilderRedirectAccess')}`);

    const shouldRenderOnboarding = sitesOnboardingEnabled
      && onboarding
      && onboarding.sites
      && !!onboarding.sites.length
      && !isInSitebuilderSsoPage;

    if (shouldRenderOnboarding) {
      sessionStorage.setItem('onboardingRendered', String(shouldRenderOnboarding));
    }

    const onboardingRendered = sessionStorage.getItem('onboardingRendered');
    const onboardingFinished = sessionStorage.getItem('onboardingFinished');

    if (shouldRenderOnboarding && !onboardingFinished) {
      return (
        <Switch>
          <Route exact path={`${t('routes.onboarding')}`} component={OnboardingHandler} />
          <Redirect to={`${t('routes.onboarding')}`} />
          {allRoutes}
        </Switch>
      );
    }


    if (ssoRedirectWHMP) {
      return (
        <Switch>
          <Route exact path={`${t('routes.whmRedirectAccess')}/:id`} component={RedirectToWHMPage} />
          <Route path="*" render={() => <Error404Page />} />
        </Switch>
      );
    }

    if (ssoRedirectSiteBuilder) {
      return (
        <Switch>
          <Route exact path={`${t('routes.siteBuilderRedirectAccess')}/:domain/:hostingId?`} render={() => <RedirectToSiteBuilderPage />} />
          <Route path="*" render={() => <Error404Page />} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && !onboardingRendered && (
      (bravoEnableEmailFlow && featureToggleEmailNumberFlow && !isTitanUrl && (!emailFlowSkip && !emailVerified)) || (emailChangeFlow)
    )) {
      return (
        <Switch>
          <Route exact path={`${t('routes.emailVerify')}/:changeFlow?`} component={EmailVerify} />
          <Redirect to={`${t('routes.emailVerify')}${emailChangeFlow ? '/alterar' : ''}?location=${getLocationValue()}`} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && !onboardingRendered && ((bravoEnablePhoneFlow && featureTogglePhoneNumberFlow && !isTitanUrl && (!phoneNumberVerified && !phoneNumberFlowSkip)) || (phoneNumberChangeFlow))) {
      return (
        <Switch>
          <Route exact path={`${t('routes.phoneVerify')}/:changeFlow?`} render={() => <PhoneVerify />} />
          <Redirect to={`${t('routes.phoneVerify')}${phoneNumberChangeFlow ? '/alterar' : ''}?location=${getLocationValue()}`} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && newSiteFlux) {
      return (
        <Switch>
          <Route exact path={t('routes.newSite')} render={() => <NewSitePage />} />
          <Route exact path={`${t('routes.newSite')}${t('routes.feedbackSite')}`} render={() => <FeedbackSite />} />
          <Redirect to={t('routes.newSite')} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && forceUrl) {
      return (
        <Switch>
          {allRoutes}
          <Redirect to={`${t('routes.myData')}`} />
        </Switch>
      );
    }

    return (
      <>
        <Switch>
          {redirects(t).map(({ path, redirectPath }, index) => (
            <Redirect
              exact
              from={path}
              to={redirectPath}
              key={`redirect-${index}-${path}`}
            />
          ))}

          {allRoutes}

          {!shouldRenderOnboarding && forceUrl && (
          <Redirect to={forceUrl} />
          )}
        </Switch>
      </>
    );
  };

  if (isRootRoute && products && (products.loaded === false || products.loading)) {
    return <Loader />;
  }

  return (
    <>
      {!currentAiChat?.fullScreen && handleAiChatRender()}

      {getRender()}
    </>
  );
};

export default withI18n()(Main);
